import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  Divider,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Save,
  X,
  Clock
} from 'lucide-react';

const complexityOptions = [
  { value: null, label: 'Non définie' },
  { value: 'LOW', label: 'Simple' },
  { value: 'MEDIUM', label: 'Moyen' },
  { value: 'HIGH', label: 'Complexe' },
  { value: 'COMPLEX', label: 'Très complexe' }
];

// Color palette for dynamic hour types
const hourTypeColors = [
  '#3b82f6', '#10b981', '#f97316', '#8b5cf6', '#ec4899',
  '#06b6d4', '#eab308', '#ef4444', '#14b8a6', '#6366f1'
];

function QuoteItemEditor({ item, onSave, onCancel, hourTypes = [] }) {
  // Initialize hours from item.hours array
  const initialHours = useMemo(() => {
    if (!item.hours || item.hours.length === 0) return {};
    return item.hours.reduce((acc, h) => {
      acc[h.hourTypeId] = h.hours || 0;
      return acc;
    }, {});
  }, [item.hours]);

  const [formData, setFormData] = useState({
    name: item.name || '',
    description: item.description || '',
    complexity: item.complexity || null,
    hours: initialHours,
    notes: item.notes || '',
    isOptional: item.isOptional || false,
    isIncluded: item.isIncluded || false
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleHoursChange = (hourTypeId, value) => {
    const numValue = parseFloat(value) || 0;
    setFormData(prev => ({
      ...prev,
      hours: {
        ...prev.hours,
        [hourTypeId]: numValue >= 0 ? numValue : 0
      }
    }));
  };

  // Calculate total hours
  const totalHours = useMemo(() => {
    return Object.values(formData.hours).reduce((sum, h) => sum + (h || 0), 0);
  }, [formData.hours]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      name: formData.name,
      description: formData.description,
      complexity: formData.complexity,
      notes: formData.notes,
      isOptional: formData.isOptional,
      isIncluded: formData.isIncluded,
      hours: formData.hours
    });
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Stack spacing={2.5}>
        {/* Basic Info */}
        <Stack direction="row" spacing={2}>
          <TextField
            fullWidth
            size="small"
            label="Nom de l'élément"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            required
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Complexité</InputLabel>
            <Select
              value={formData.complexity || ''}
              label="Complexité"
              onChange={(e) => handleChange('complexity', e.target.value || null)}
            >
              {complexityOptions.map(opt => (
                <MenuItem key={opt.value || 'none'} value={opt.value || ''}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <TextField
          fullWidth
          size="small"
          label="Description"
          multiline
          rows={2}
          value={formData.description}
          onChange={(e) => handleChange('description', e.target.value)}
          placeholder="Détails de l'élément, spécifications..."
        />

        {/* Hours Breakdown */}
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block', fontWeight: 600, letterSpacing: 0.5 }}>
            VENTILATION DES HEURES
          </Typography>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            {hourTypes.map((ht, idx) => {
              const color = hourTypeColors[idx % hourTypeColors.length];
              return (
                <TextField
                  key={ht.id}
                  size="small"
                  type="number"
                  label={ht.name}
                  value={formData.hours[ht.id] || 0}
                  onChange={(e) => handleHoursChange(ht.id, e.target.value)}
                  inputProps={{ min: 0, step: 0.5 }}
                  sx={{ 
                    width: 130,
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: `${color}30` },
                      '&:hover fieldset': { borderColor: `${color}60` },
                      '&.Mui-focused fieldset': { borderColor: color }
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box sx={{ color, display: 'flex' }}><Clock size={16} /></Box>
                      </InputAdornment>
                    ),
                    endAdornment: <InputAdornment position="end">h</InputAdornment>
                  }}
                />
              );
            })}
          </Stack>
          
          {/* Total */}
          <Box sx={{ 
            mt: 2, 
            p: 1.5, 
            bgcolor: 'rgba(0, 212, 170, 0.05)', 
            borderRadius: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Total estimé
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#00d4aa' }}>
              {totalHours}h
            </Typography>
          </Box>
        </Box>

        {/* Optional/Included Toggles */}
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block', fontWeight: 600, letterSpacing: 0.5 }}>
            OPTIONS
          </Typography>
          <Stack direction="row" spacing={3}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={formData.isIncluded}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    isIncluded: e.target.checked,
                    isOptional: e.target.checked ? false : prev.isOptional
                  }))}
                  color="success"
                />
              }
              label={<Typography variant="body2" sx={{ color: formData.isIncluded ? '#10b981' : 'inherit' }}>Heures offertes</Typography>}
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={formData.isOptional}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    isOptional: e.target.checked,
                    isIncluded: e.target.checked ? false : prev.isIncluded
                  }))}
                  color="warning"
                />
              }
              label={<Typography variant="body2" sx={{ color: formData.isOptional ? '#f59e0b' : 'inherit' }}>Optionnel</Typography>}
            />
          </Stack>
        </Box>

        {/* Notes */}
        <TextField
          fullWidth
          size="small"
          label="Notes internes"
          multiline
          rows={2}
          value={formData.notes}
          onChange={(e) => handleChange('notes', e.target.value)}
          placeholder="Notes pour l'équipe (non visible par le client)..."
          sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255, 255, 255, 0.02)' } }}
        />

        {/* Actions */}
        <Divider sx={{ opacity: 0.1 }} />
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            variant="outlined"
            size="small"
            startIcon={<X size={14} />}
            onClick={onCancel}
            color="inherit"
          >
            Annuler
          </Button>
          <Button
            type="submit"
            variant="contained"
            size="small"
            startIcon={<Save size={14} />}
            disabled={!formData.name.trim()}
          >
            Enregistrer
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

export default QuoteItemEditor;
